import { Link } from "@mui/material";

import {
  faqs,
  howItWorks,
  TCListItems,
  CheckoutTerms,
  AnalyticsHeader,
  RegistrationTerms,
} from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/background.jpg";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/Groups_Banner_1486x148.png";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/Blue%20Jays/Toronto_Blue_Jay_Primary_Logo.svg.png";

const config: TeamConfig = {
  teamSchema: "torontobluejays_groups",
  browserTitle: "Blue Jays Groups",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Toronto Blue Jays",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-ca-10001-bluejays-sec",
    mapId: "blockmap2d",
    isGroupTicketSeatMapEnabled: true,
    // no 3d view published for these sections (403)
    isView3dButtonHiddenBySectionName: [
      "210",
      "211",
      "212",
      "213",
      "214",
      "215",
      "216",
      "217",
      "218",
      "219",
      "220",
      "221",
      "222",
      "223",
      "224",
      "225",
      "226",
      "227",
      "228",
      "229",
      "230",
      "231",
      "232",
      "233",
      "234",
      "235",
      "236",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 12,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 12,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,

    isTicketTransferEnabled: false,

    checkout: {
      totalAmountBreakdown: {
        isEnabled: true,
        totalAmountLabel: "Total Amount",
        ticketTotalLabel: "Tickets Total",
        ticketFeesLabel: "Ticket Fees",
        taxesLabel: "Taxes",
      },
    },
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-N8YFCZLYSZ",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/tbjrc/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: BackgroundImage,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
      showPriceDecimals: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,
  },

  colors: {
    primary: "#134A8E",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <TCListItems />,
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <RegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Select Continue to view seating options.",
      purchaseSeatsLanguageDisclaimer: (
        <>
          Exact seat location can be seen on the checkout screen. Contact us at{" "}
          <Link
            href="tel:4163411234"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            (416) 341-1234
          </Link>{" "}
          if you have any questions.
        </>
      ),
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Your Toronto Blue Jays tickets are confirmed!",

    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in your group. Minimum of 12 required.",
      },
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
